import { queryOptions } from "@tanstack/react-query";
import fetcher from "@/core/lib/fetcher";
import type { APIResponse } from "@/core/types/utils";
import type { GroupPerformanceData } from "./types";

const emptyReport: GroupPerformanceData = {
	summary: {
		totalBookings: { count: 0, revenue: 0 },
		rooms: { count: 0, revenue: 0 },
		activitiesProducts: { count: 0, revenue: 0 },
		packages: { count: 0, revenue: 0 },
	},
	properties: [],
	trend: [],
	promoCodes: [],
};

export const groupMetricsQueryOptions = (
	token: string,
	groupId: string,
	startDate: string,
	endDate: string,
	propertyIds: string[],
) =>
	queryOptions({
		queryKey: [
			"group-metrics",
			groupId,
			startDate,
			endDate,
			propertyIds,
			token,
		],
		queryFn: () => {
			const propertiesParam =
				propertyIds.length > 0 ? `&properties=${propertyIds.join(",")}` : "";
			return fetcher
				.auth(`Bearer ${token}`)
				.get(
					import.meta.env.VITE_API_BOOKING +
						`property/bookings/group/${groupId}/metrics?startDate=${startDate}&endDate=${endDate}&origin=Web${propertiesParam}`,
				)
				.notFound(() => ({ data: emptyReport }))
				.json<Promise<APIResponse<GroupPerformanceData>>>();
		},
	});

function buildExportUrl(
	format: "pdf" | "xlsx",
	groupId: string,
	startDate: string,
	endDate: string,
	propertyIds: string[],
	name: string,
) {
	const propertiesParam =
		propertyIds.length > 0 ? `&properties=${propertyIds.join(",")}` : "";
	const nameParam = name ? `&name=${encodeURIComponent(name)}` : "";
	return (
		import.meta.env.VITE_API_BOOKING +
		`property/bookings/group/${groupId}/metrics/${format}?startDate=${startDate}&endDate=${endDate}&origin=Web${propertiesParam}${nameParam}`
	);
}

export const downloadGroupReportPdf = (
	token: string,
	groupId: string,
	startDate: string,
	endDate: string,
	propertyIds: string[],
	name: string,
) =>
	fetcher
		.auth(`Bearer ${token}`)
		.get(buildExportUrl("pdf", groupId, startDate, endDate, propertyIds, name))
		.blob();

export const downloadGroupReportXlsx = (
	token: string,
	groupId: string,
	startDate: string,
	endDate: string,
	propertyIds: string[],
	name: string,
) =>
	fetcher
		.auth(`Bearer ${token}`)
		.get(buildExportUrl("xlsx", groupId, startDate, endDate, propertyIds, name))
		.blob();
